import { useEffect, useState } from "react";
import { ShieldCheck, Clock, XCircle, FileText, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  document_type: "Aadhaar Card",
  document_number: "",
  dob: "",
  pan_number: "",
  bank_name: "",
  account_holder: "",
  account_number: "",
  ifsc: "",
  nominee_name: "",
  nominee_relation: "",
  nominee_dob: "",
};

const DOC_TYPES = ["Aadhaar Card", "Passport", "Voter ID", "Driving License"];

const STATUS_CFG = {
  pending: { label: "Pending Review", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Clock },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-800 border-red-200", Icon: XCircle },
};

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-white shadow-lg p-5 sm:p-6">
      <h2 className="font-extrabold text-lg text-slate-900 mb-4"
          style={{ fontFamily: "Outfit, sans-serif" }}>
        {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-500 outline-none";

export default function KYC() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState(null);
  const [adminNote, setAdminNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/kyc/my");
      if (data.kyc) {
        const { status, admin_note, ...rest } = data.kyc;
        setForm({
          document_type: rest.document_type || "Aadhaar Card",
          document_number: rest.document_number || "",
          dob: rest.dob || "",
          pan_number: rest.pan_number || "",
          bank_name: rest.bank_name || "",
          account_holder: rest.account_holder || "",
          account_number: rest.account_number || "",
          ifsc: rest.ifsc || "",
          nominee_name: rest.nominee_name || "",
          nominee_relation: rest.nominee_relation || "",
          nominee_dob: rest.nominee_dob || "",
        });
        setStatus(status);
        setAdminNote(admin_note);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "verified") return;
    setSubmitting(true);
    try {
      await api.post("/kyc", form);
      toast.success("KYC submitted — awaiting admin verification");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const readOnly = status === "verified";
  const cfg = status ? STATUS_CFG[status] : null;

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">
        <div className="bg-white rounded-2xl shadow-lg p-6 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2"
                style={{ fontFamily: "Outfit, sans-serif" }}>
              <ShieldCheck size={26} className="text-red-600" />
              KYC Verification
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              Complete your identity, PAN, bank & nominee details for withdrawal eligibility
            </p>
          </div>
          {cfg && (
            <span
              data-testid="kyc-status-pill"
              className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-full border ${cfg.cls}`}
            >
              <cfg.Icon size={14} /> {cfg.label}
            </span>
          )}
        </div>

        {status === "rejected" && adminNote && (
          <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
            <b>Admin note:</b> {adminNote}
          </div>
        )}

        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="h-8 w-8 rounded-full border-4 border-white/40 border-t-white animate-spin" />
          </div>
        ) : (
          <form data-testid="kyc-form" onSubmit={submit} className="space-y-4">
            <fieldset disabled={readOnly} className="space-y-4">
              <Section title="Identity">
                <Field label="Document Type">
                  <select data-testid="kyc-document-type" value={form.document_type}
                          onChange={change("document_type")} className={inputCls} required>
                    {DOC_TYPES.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Document Number">
                  <input data-testid="kyc-document-number" value={form.document_number}
                         onChange={change("document_number")} className={inputCls} required />
                </Field>
                <Field label="Date of Birth">
                  <input data-testid="kyc-dob" type="date" value={form.dob}
                         onChange={change("dob")} className={inputCls} />
                </Field>
                <Field label="PAN Number (optional)">
                  <input data-testid="kyc-pan" value={form.pan_number}
                         onChange={change("pan_number")} className={`${inputCls} uppercase`} />
                </Field>
              </Section>

              <Section title="Bank Details">
                <Field label="Bank Name">
                  <input data-testid="kyc-bank-name" value={form.bank_name}
                         onChange={change("bank_name")} className={inputCls} required />
                </Field>
                <Field label="Account Holder Name">
                  <input data-testid="kyc-account-holder" value={form.account_holder}
                         onChange={change("account_holder")} className={inputCls} required />
                </Field>
                <Field label="Account Number">
                  <input data-testid="kyc-account-number" value={form.account_number}
                         onChange={change("account_number")} className={inputCls} required />
                </Field>
                <Field label="IFSC Code">
                  <input data-testid="kyc-ifsc" value={form.ifsc}
                         onChange={change("ifsc")} className={`${inputCls} uppercase`} required />
                </Field>
              </Section>

              <Section title="Nominee (optional)">
                <Field label="Nominee Name">
                  <input data-testid="kyc-nominee-name" value={form.nominee_name}
                         onChange={change("nominee_name")} className={inputCls} />
                </Field>
                <Field label="Relation">
                  <input data-testid="kyc-nominee-relation" value={form.nominee_relation}
                         onChange={change("nominee_relation")} placeholder="e.g. Spouse, Father"
                         className={inputCls} />
                </Field>
                <Field label="Nominee DOB">
                  <input data-testid="kyc-nominee-dob" type="date" value={form.nominee_dob}
                         onChange={change("nominee_dob")} className={inputCls} />
                </Field>
              </Section>
            </fieldset>

            <div className="flex justify-end">
              <button
                data-testid="kyc-submit-btn"
                type="submit"
                disabled={submitting || readOnly}
                className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white font-semibold rounded-lg px-6 py-3 shadow-md active:scale-[0.99] disabled:opacity-60 transition"
              >
                <FileText size={16} />
                {readOnly ? "KYC Verified" : submitting ? "Submitting..." : status ? "Resubmit KYC" : "Submit KYC"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
